/* eslint-disable max-len */
/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
import {
    assertEvent, assign, raise, setup,
} from 'xstate';
import {
    ServiceShortName,
    ReportedFindingTransform,
    ReportedFindingDataMachine,
    TrackingAction,
} from '@acp/shared';
import { produce } from 'immer';
import { UseToastOptions } from '@chakra-ui/react';

import { reportedFindingsWizardActors } from './actors';

type ReportedWizardThirdPartyDocument = {
    id: string;
    name: string;
    size: number;
};

type ReportedFindingsWizardEvents = { type: 'OPEN'; compliancePropertyId: number; serviceShortName: ServiceShortName | string }
| { type: 'SEND_QUOTE_TO_LANDLORD' }
| { type: 'FORWARD_QUOTE'; quotationId: number }
| { type: 'REFRESH' }
| { type: 'WINDOW_REFOCUS' }
| { type: 'QUOTE_FORWARDED'; quotationId: number }
| { type: 'RESOLVE_VIA_THIRD_PARTY'; reportedFindingId: number }
| { type: 'REQUEST_QUOTE'; reportedFindingId: number }
| { type: 'NEXT' }
| { type: 'BACK' }
| {
    type: 'SUBMIT_THIRD_PARTY_RESOLUTION';
    thirdPartyResolutionForm: {
        notes: string;
        documents: ReportedWizardThirdPartyDocument[];
        selectedProblemRegisterIds: number[];
    };
}
| { type: 'SUBMIT_THIRD_PARTY_SELECTION'; selectedProblemRegisterIds: number[] }
| { type: 'SUBMIT_QUOTE_REQUEST' }
| { type: 'CLOSE_MODAL' }
| { type: 'FORCE_CLOSE_MODAL' }
| { type: 'SHOW_CLOSE_CONFIRMATION' }
| { type: 'HIDE_CLOSE_CONFIRMATION' }
| { type: 'RESOLVE_MORE' }
| { type: 'CONTINUE' }
| { type: 'CONFIRM' }
| { type: 'TOGGLE_REPORTED_FINDING'; reportedFindingId: number }
| { type: 'FATAL_ERROR'; message: string }
| { type: 'DISMISS_FINDING'; reportedFindingId: number }
| { type: 'UN_DISMISS_FINDING'; reportedFindingId: number }
| { type: 'INFORM_DI'; reportedFindingId: number }
| { type: 'INFORM_DI_CONFIRM'; informDIForm: { notes: string } }
| { type: 'ENTER_PROPERTY_BUILT_YEAR' }
| { type: 'ENTER_BUILT_YEAR_CONFIRM'; builtYearForm: { notes: string; propertyBuilt: Date } };

const reportedFindingsWizardMachine = setup({
    types: {
        events: {} as ReportedFindingsWizardEvents,
        context: {} as {
            compliancePropertyId: number;
            selectedItems: number[];
            serviceShortName: ServiceShortName | string;
            quoteIdForwardingToLandlord: number;
            quoteIdsForwardedToLandlord: number[];
            reportedFindings: ReportedFindingDataMachine[];
            apiResponse: ReportedFindingTransform | null;
            fatalErrorMessage: string | null;
            showCloseConfirmation: boolean;
            thirdPartyResolutionForm: {
                selectedProblemRegisterIds: number[];
                notes: string;
                documents: ReportedWizardThirdPartyDocument[];
            };
            informDIForm: {
                notes: string;
            };
            builtYearForm: {
                notes?: string;
                propertyBuilt?: Date; // optional for initial state but required for form level validation - Date objects (from DatePickerField)
            };
            showBuiltYearInput: boolean;
            dismissReportedFindingId: number | null;
        },
        input: {} as {
            compliancePropertyId: number;
            serviceShortName: ServiceShortName | string;
        },
    },
    actors: reportedFindingsWizardActors,
    actions: {
        onAddTracking: (_, _params: { data: Record<string, unknown>; trackingType: TrackingAction }) => {},
        onFatalError: (_, _params: { message: string }) => {},
        resetCache: () => {},
        resetWizard: assign((
            { context }
        ) => produce(context, (draft) => {
            draft.selectedItems = [];
            draft.fatalErrorMessage = null;
            draft.showCloseConfirmation = false;
            draft.reportedFindings = [];
            draft.apiResponse = null;
            draft.quoteIdsForwardedToLandlord = [];
            draft.quoteIdForwardingToLandlord = -1;
            draft.thirdPartyResolutionForm = {
                selectedProblemRegisterIds: [],
                documents: [],
                notes: '',
            };
            draft.informDIForm = {
                notes: '',
            };
            draft.builtYearForm = {
                notes: '',
                propertyBuilt: undefined,
            };
        })),

        // called once we have reported findings data from the API
        setupWizardFromApiResponse: assign((
            { context },
            params: { apiResponse: ReportedFindingTransform }
        ) => produce(context, (draft) => {
            // We're resetting quoteIdsForwardedToLandlord as we want it
            // to persist between refreshes
            draft.selectedItems = [];
            draft.fatalErrorMessage = null;
            draft.showCloseConfirmation = false;
            draft.thirdPartyResolutionForm = {
                selectedProblemRegisterIds: [],
                documents: [],
                notes: '',
            };
            draft.reportedFindings = [...params?.apiResponse?.reportedFindings ?? []];
            draft.informDIForm = {
                notes: '',
            };
            draft.builtYearForm = {
                notes: '',
            };
            draft.apiResponse = params.apiResponse;
        })),

        showCloseConfirmation: assign(({ context }) => produce(context, (draft) => {
            draft.showCloseConfirmation = true;
        })),
        hideCloseConfirmation: assign(({ context }) => produce(context, (draft) => {
            draft.showCloseConfirmation = false;
        })),
        onToast: (_, _params: UseToastOptions) => {},
    },
    guards: {
        isMinimumStandardsService: ({ context }) => context.serviceShortName as ServiceShortName === ServiceShortName.MinimumStandards,
    },
}).createMachine({
    id: 'reportedFindingsWizard',
    initial: 'idle',
    context: ({ input }) => ({
        compliancePropertyId: input.compliancePropertyId,
        serviceShortName: input.serviceShortName,
        reportedFindings: [],
        quoteIdForwardingToLandlord: -1,
        quoteIdsForwardedToLandlord: [],
        apiResponse: null,
        fatalErrorMessage: null,
        selectedItems: [],
        showCloseConfirmation: false,
        thirdPartyResolutionForm: {
            selectedProblemRegisterIds: [],
            documents: [],
            notes: '',
        },
        informDIForm: {
            notes: '',
        },
        builtYearForm: {
            notes: '',
            propertyBuilt: undefined,
        },
        dismissReportedFindingId: null,
        showBuiltYearInput: false,
    }),
    on: {
        FATAL_ERROR: {
            description: 'A fatal error occurred',
            actions: [assign(({ context, event }) => produce(context, (draft) => {
                assertEvent(event, 'FATAL_ERROR');
                draft.fatalErrorMessage = event.message;
            })), {
                type: 'onFatalError',
                params: ({ event }) => ({ message: event.message }),
            }],
            target: '.fatalError',
        },
    },
    states: {
        idle: {
            entry: {
                type: 'resetWizard',
            },
            on: {
                OPEN: {
                    target: 'loadReportedFindings',
                    actions: [assign(({ context, event }) => produce(context, (draft) => {
                        assertEvent(event, 'OPEN');
                        draft.compliancePropertyId = event.compliancePropertyId;
                    })), {
                        type: 'onAddTracking',
                        params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardOpened }),
                    }],
                },
            },
        },
        loadReportedFindings: {
            description: 'Retrieve reported findings data via API',
            invoke: {
                src: 'getReportedFindingsByProperty',
                input: ({ context }) => ({
                    compliancePropertyId: context.compliancePropertyId,
                    serviceShortname: context.serviceShortName,
                }),
                onDone: [
                    {
                        // eslint-disable-next-line max-len
                        guard: ({ event }) => event.output === null || event.output?.reportedFindings?.length === 0,
                        target: 'noReportedFindings',
                        actions: {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardNoReportedFindings }),
                        },
                    },
                    {
                        guard: ({ event }) => (event?.output !== null && event?.output?.reportedFindings?.length > 0),
                        description: 'Reported findings found',
                        actions: {
                            type: 'setupWizardFromApiResponse',
                            params: ({ event }) => ({
                                apiResponse: event.output,
                            }),
                        },
                        target: 'selectResolutionMethod',
                    },
                ],
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        noReportedFindings: {
            on: {
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
            },
        },
        selectResolutionMethod: {
            on: {
                REQUEST_QUOTE: {
                    target: 'selectRequestQuoteItems',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.selectedItems = [event.reportedFindingId];
                    })),
                },
                RESOLVE_VIA_THIRD_PARTY: [
                    {
                        guard: 'isMinimumStandardsService',
                        target: 'thirdPartyResolutionForm',
                        actions: [(assign(({ context, event }) => produce(context, (draft) => {
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds = [
                                event.reportedFindingId,
                            ];
                        }))),
                        {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: { selectedProblemRegisterIds: context.thirdPartyResolutionForm.selectedProblemRegisterIds }, trackingType: TrackingAction.ReportedFindingsWizardResolveByThirdPartyStarted }),
                        }],
                    },
                    {
                        target: 'selectThirdPartyItems',
                        actions: [(assign(({ context, event }) => produce(context, (draft) => {
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds = [
                                event.reportedFindingId,
                            ];
                        }))),
                        {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: { selectedProblemRegisterIds: context.thirdPartyResolutionForm.selectedProblemRegisterIds }, trackingType: TrackingAction.ReportedFindingsWizardResolveByThirdPartyStarted }),
                        }],
                    },
                ],
                WINDOW_REFOCUS: 'loadReportedFindings',
                INFORM_DI: {
                    target: 'informDI',
                    actions: [(assign(({ context, event }) => produce(context, (draft) => {
                        draft.selectedItems = [event.reportedFindingId];
                        draft.showBuiltYearInput = false;
                    }))),
                    {
                        type: 'onAddTracking',
                        params: ({ context }) => ({ data: { informDIForm: { ...context.informDIForm } }, trackingType: TrackingAction.ReportedFindingsWizardInformDiStarted }),
                    }],
                },
                ENTER_PROPERTY_BUILT_YEAR: {
                    target: 'enterBuiltYear',
                    actions: [(assign(({ context }) => produce(context, (draft) => {
                        draft.selectedItems = context.reportedFindings
                            .filter((item) => item.problemRegister.canEnterBuiltYear === true)
                            .map((item) => item.problemRegister.problemRegisterId);
                        draft.showBuiltYearInput = true;
                    }))),
                    {
                        type: 'onAddTracking',
                        params: ({ context }) => ({ data: { builtYearForm: { ...context.builtYearForm } }, trackingType: TrackingAction.ReportedFindingsWizardBuildYearStarted }),
                    }],
                },
                REFRESH: 'loadReportedFindings',
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                FORWARD_QUOTE: {
                    target: 'forwardQuoteToLandlord',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: ({ event }) => ({ data: { quotationId: event.quotationId }, trackingType: TrackingAction.ReportedFindingsWizardForwardQuote }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                DISMISS_FINDING: {
                    target: 'dismissFinding',
                },
                UN_DISMISS_FINDING: {
                    target: 'dismissFinding',
                },
            },
        },
        forwardQuoteToLandlord: {
            on: {
                QUOTE_FORWARDED: {
                    target: 'selectResolutionMethod',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.quoteIdsForwardedToLandlord.push(event.quotationId);
                    })),
                },
                BACK: {
                    target: 'selectResolutionMethod',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.quoteIdForwardingToLandlord = -1;
                    })),
                },
            },
        },
        selectRequestQuoteItems: {
            on: {
                TOGGLE_REPORTED_FINDING: {
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        // If the selectedCreateQuote items has the item we're toggling,
                        // we'll remove it,
                        // otherwise, we'll add it
                        const existingItemIndex = draft.selectedItems.findIndex(
                            (x) => x === event.reportedFindingId
                        );

                        const hasReportedFinding = existingItemIndex !== -1;
                        if (hasReportedFinding) {
                            draft.selectedItems.splice(existingItemIndex, 1);
                        } else {
                            draft.selectedItems.push(event.reportedFindingId);
                        }
                    })),
                },
                BACK: 'selectResolutionMethod',
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                NEXT: 'confirmRequestQuote',
            },
        },
        confirmRequestQuote: {
            on: {
                BACK: 'selectRequestQuoteItems',
                SUBMIT_QUOTE_REQUEST: {
                    target: 'requestQuote',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: { selectedReportedFindingIds: context.selectedItems }, trackingType: TrackingAction.ReportedFindingsWizardRequestQuote }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
            },
        },
        requestQuote: {
            invoke: {
                src: 'requestQuoteReportedFindings',
                input: ({ context }) => ({
                    compliancePropertyId: context.compliancePropertyId,
                    problemRegisterIds: context.selectedItems,
                }),
                onDone: {
                    target: 'quoteRequestComplete',
                    actions: 'hideCloseConfirmation',
                },
                onError: {
                    target: 'selectRequestQuoteItems',
                    actions: [
                        {
                            type: 'onToast',
                            params: {
                                title: 'Request quote failed',
                                description: 'Failed to request quote for the selected findings',
                                status: 'error',
                            },
                        },
                        raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                        'hideCloseConfirmation',
                    ],
                },
            },
        },
        quoteRequestComplete: {
            on: {
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [{
                        type: 'onAddTracking',
                        params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                    },
                    {
                        type: 'resetCache',
                    },
                    ],
                },
                RESOLVE_MORE: 'loadReportedFindings',
            },
        },
        selectThirdPartyItems: {
            on: {
                TOGGLE_REPORTED_FINDING: {
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        // If the selectedCreateQuote items has the item we're toggling,
                        // we'll remove it,
                        // otherwise, we'll add it
                        const existingItemIndex = draft.thirdPartyResolutionForm
                            .selectedProblemRegisterIds.findIndex(
                                (x) => x === event.reportedFindingId
                            );

                        const hasReportedFinding = existingItemIndex !== -1;

                        if (hasReportedFinding) {
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds
                                .splice(existingItemIndex, 1);
                        } else {
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds
                                .push(event.reportedFindingId);
                        }
                    })),
                },
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                CONTINUE: 'thirdPartyResolutionForm',
                BACK: 'selectResolutionMethod',
            },
        },
        thirdPartyResolutionForm: {
            on: {
                BACK: [
                    {
                        guard: 'isMinimumStandardsService',
                        target: 'selectResolutionMethod',
                    },
                    {
                        target: 'selectThirdPartyItems',
                    },
                ],
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                SUBMIT_THIRD_PARTY_RESOLUTION: [
                    {
                        guard: 'isMinimumStandardsService',
                        target: 'submitResolution',
                        actions: [assign(({ context, event }) => produce(context, (draft) => {
                            draft.thirdPartyResolutionForm.notes = event.thirdPartyResolutionForm.notes;
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds = context.thirdPartyResolutionForm.selectedProblemRegisterIds;
                            // eslint-disable-next-line max-len
                            draft.thirdPartyResolutionForm.documents = event.thirdPartyResolutionForm.documents;
                        })), {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: context.thirdPartyResolutionForm, trackingType: TrackingAction.ReportedFindingsWizardResolveByThirdPartySelectionContinue }),
                        }],
                    },
                    {
                        target: 'thirdPartyDisclaimer',
                        actions: [assign(({ context, event }) => produce(context, (draft) => {
                            draft.thirdPartyResolutionForm.notes = event.thirdPartyResolutionForm.notes;
                            draft.thirdPartyResolutionForm.selectedProblemRegisterIds = context.thirdPartyResolutionForm.selectedProblemRegisterIds;
                            // eslint-disable-next-line max-len
                            draft.thirdPartyResolutionForm.documents = event.thirdPartyResolutionForm.documents;
                        })), {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: context.thirdPartyResolutionForm, trackingType: TrackingAction.ReportedFindingsWizardResolveByThirdPartySelectionContinue }),
                        }],
                    },
                ],
            },
        },
        thirdPartyDisclaimer: {
            on: {
                BACK: 'thirdPartyResolutionForm',
                CONFIRM: 'submitResolution',
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'showCloseConfirmation',
                        },
                    ],
                },
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                        {
                            type: 'hideCloseConfirmation',
                        },
                    ],
                },
            },
        },
        submitResolution: {
            invoke: {
                src: 'submitReportedFindings',
                input: ({ context }) => ({
                    compliancePropertyId: context.compliancePropertyId,
                    problemRegisterId: context.thirdPartyResolutionForm.selectedProblemRegisterIds,
                    documentIds: context.thirdPartyResolutionForm.documents.map((x) => x.id),
                    notes: context.thirdPartyResolutionForm.notes,
                }),
                onDone: {
                    target: 'thirdPartyComplete',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: ({ context }) => ({ data: context.thirdPartyResolutionForm, trackingType: TrackingAction.ReportedFindingsWizardResolveByThirdPartyDisclaimer }),
                        },
                    ],
                },
                onError: [
                    {
                        target: 'thirdPartyDisclaimer',
                        actions: [
                            {
                                type: 'onToast',
                                params: {
                                    title: 'Submit failed',
                                    description: 'Failed to submit reported finding resolution',
                                    status: 'error',
                                },
                            },
                            raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                        ],
                    },
                ],
            },
        },
        thirdPartyError: {
            on: {
                FORCE_CLOSE_MODAL: 'idle',
            },
        },
        thirdPartyComplete: {
            on: {
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
                RESOLVE_MORE: 'loadReportedFindings',
            },
        },
        dismissFinding: {
            initial: 'confirm',
            entry: assign(({ context, event }) => produce(context, (draft) => {
                assertEvent(event, ['DISMISS_FINDING', 'UN_DISMISS_FINDING']);
                draft.dismissReportedFindingId = event.reportedFindingId;
            })),
            onDone: {
                target: 'loadReportedFindings',
            },
            states: {
                confirm: {
                    on: {
                        BACK: 'done',
                        DISMISS_FINDING: {
                            target: 'dismissing',
                            actions: [
                                {
                                    type: 'onAddTracking',
                                    params: ({ event }) => ({ data: { reportedFindingId: event.reportedFindingId }, trackingType: TrackingAction.ReportedFindingsWizardDismissed }),
                                },
                                {
                                    type: 'resetCache',
                                },
                            ],
                        },
                        UN_DISMISS_FINDING: {
                            target: 'unDismissing',
                            actions: [
                                {
                                    type: 'onAddTracking',
                                    params: ({ event }) => ({ data: { reportedFindingId: event.reportedFindingId }, trackingType: TrackingAction.ReportedFindingsWizardUndismissed }),
                                },
                                {
                                    type: 'resetCache',
                                },
                            ],
                        },
                    },
                },
                dismissing: {
                    invoke: {
                        src: 'dismissFindingRequest',
                        input: ({ event }) => {
                            assertEvent(event, 'DISMISS_FINDING');

                            return {
                                problemRegisterId: event.reportedFindingId,
                            };
                        },
                        onDone: [
                            {
                                target: 'done',
                                actions: [
                                    {
                                        type: 'onToast',
                                        params: {
                                            title: 'Successfully Dismissed',
                                            description: 'This finding has been dismissed',
                                            status: 'success',
                                        },
                                    },
                                ],
                            },
                        ],
                        onError: [
                            {
                                target: 'done',
                                actions: [
                                    {
                                        type: 'onToast',
                                        params: {
                                            title: 'Dismiss Failed',
                                            description: 'Failed to dismiss the finding',
                                            status: 'error',
                                        },
                                    },
                                    raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                                ],
                            },
                        ],
                    },
                },
                unDismissing: {
                    invoke: {
                        src: 'unDismissFindingRequest',
                        input: ({ event }) => {
                            assertEvent(event, 'UN_DISMISS_FINDING');

                            return {
                                problemRegisterId: event.reportedFindingId,
                            };
                        },
                        onDone: [
                            {
                                target: 'done',
                                actions: [
                                    {
                                        type: 'onToast',
                                        params: {
                                            title: 'Successfully Restored',
                                            description: 'This finding has been restored',
                                            status: 'success',
                                        },
                                    },
                                ],
                            },
                        ],
                        onError: [
                            {
                                target: 'done',
                                actions: [
                                    {
                                        type: 'onToast',
                                        params: {
                                            title: 'Restore Failed',
                                            description: 'Failed to restore the finding',
                                            status: 'error',
                                        },
                                    },
                                    raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                                ],
                            },
                        ],
                    },
                },
                done: {
                    type: 'final',
                },
            },

        },
        informDI: {
            initial: 'selectItems',
            target: 'loadReportedFindings',
            onDone: [
                {
                    guard: (event) => event.event.output === 'CLOSE',
                    target: 'idle',
                },
                {
                    guard: (event) => event.event.output === 'BACK',
                    target: 'loadReportedFindings',
                },
            ],
            on: {
                BACK: 'loadReportedFindings',
            },
            states: {
                selectItems: {
                    on: {
                        TOGGLE_REPORTED_FINDING: {
                            actions: assign(({ context, event }) => produce(context, (draft) => {
                                const existingItemIndex = draft.selectedItems.findIndex(
                                    (x) => x === event.reportedFindingId
                                );

                                const hasReportedFinding = existingItemIndex !== -1;

                                if (hasReportedFinding) {
                                    draft.selectedItems.splice(existingItemIndex, 1);
                                } else {
                                    draft.selectedItems.push(event.reportedFindingId);
                                }
                            })),
                        },
                        INFORM_DI_CONFIRM: {
                            target: 'informDIRequest',
                            actions: assign(({ context, event }) => produce(context, (draft) => {
                                draft.informDIForm.notes = event.informDIForm.notes;
                            })),
                        },
                        FORCE_CLOSE_MODAL: 'done',
                        CLOSE_MODAL: 'done',
                        BACK: 'back',
                    },
                },
                informDIRequest: {
                    invoke: {
                        src: 'informDIRequest',
                        input: ({ context, event }) => {
                            assertEvent(event, 'INFORM_DI_CONFIRM');

                            return {
                                compliancePropertyId: context.compliancePropertyId,
                                problemRegisterIds: context.selectedItems,
                                notes: event.informDIForm.notes,
                            };
                        },
                        onDone: {
                            target: 'confirmed',
                            actions: [
                                {
                                    type: 'onAddTracking',
                                    params: ({ context }) => ({ data: { ...context.informDIForm, selectedProblemRegisterIds: context.selectedItems }, trackingType: TrackingAction.ReportedFindingsWizardInformDiStarted }),
                                },
                            ],
                        },
                        onError: {
                            target: 'selectItems',
                            actions: [
                                {
                                    type: 'onToast',
                                    params: {
                                        title: 'Inform DI failed',
                                        description: 'Failed to inform DI for the selected findings',
                                        status: 'error',
                                    },
                                },
                                raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                            ],
                        },
                    },
                },
                confirmed: {
                    on: {
                        FORCE_CLOSE_MODAL: 'done',
                        CLOSE_MODAL: 'done',
                        RESOLVE_MORE: 'back',
                    },
                },
                back: {
                    type: 'final',
                    output: 'BACK',
                },
                done: {
                    type: 'final',
                    output: 'CLOSE',
                },
            },
        },
        enterBuiltYear: {
            initial: 'selectItems',
            target: 'loadReportedFindings',
            onDone: [
                {
                    guard: (event) => event.event.output === 'CLOSE',
                    target: 'idle',
                },
                {
                    guard: (event) => event.event.output === 'BACK',
                    target: 'loadReportedFindings',
                },
            ],
            on: {
                BACK: 'loadReportedFindings',
            },
            states: {
                selectItems: {
                    on: {
                        ENTER_BUILT_YEAR_CONFIRM: {
                            target: 'enterBuiltYearRequest',
                            actions: assign(({ context, event }) => produce(context, (draft) => {
                                draft.builtYearForm.notes = event.builtYearForm.notes;
                                draft.builtYearForm.propertyBuilt = event.builtYearForm.propertyBuilt;
                            })),
                        },
                        FORCE_CLOSE_MODAL: 'done',
                        CLOSE_MODAL: 'done',
                        BACK: 'back',
                    },
                },
                enterBuiltYearRequest: {
                    invoke: {
                        src: 'enterBuiltYearRequest',
                        input: ({ context, event }) => {
                            assertEvent(event, 'ENTER_BUILT_YEAR_CONFIRM');

                            return {
                                compliancePropertyId: context.compliancePropertyId,
                                problemRegisterIds: context.selectedItems,
                                propertyBuilt: event.builtYearForm.propertyBuilt,
                                notes: event.builtYearForm.notes,
                            };
                        },
                        onDone: {
                            target: 'confirmed',
                            actions: [
                                {
                                    type: 'onAddTracking',
                                    params: ({ context }) => ({ data: { ...context.builtYearForm, selectedProblemRegisterIds: context.selectedItems }, trackingType: TrackingAction.ReportedFindingsWizardBuildSubmitted }),
                                },
                            ],
                        },
                        onError: {
                            target: 'selectItems',
                            actions: [
                                {
                                    type: 'onToast',
                                    params: {
                                        title: 'Enter property built year failed',
                                        description: 'Failed to enter built year for the selected findings',
                                        status: 'error',
                                    },
                                },
                                raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                            ],
                        },
                    },
                },
                confirmed: {
                    on: {
                        FORCE_CLOSE_MODAL: 'done',
                        CLOSE_MODAL: 'done',
                        RESOLVE_MORE: 'back',
                    },
                },
                back: {
                    type: 'final',
                    output: 'BACK',
                },
                done: {
                    type: 'final',
                    output: 'CLOSE',
                },
            },
        },
        fatalError: {
            on: {
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: [
                        {
                            type: 'onAddTracking',
                            params: () => ({ data: {}, trackingType: TrackingAction.ReportedFindingsWizardClosed }),
                        },
                        {
                            type: 'resetCache',
                        },
                    ],
                },
            },
        },
    },
});

export { reportedFindingsWizardMachine };
